<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import KanbanCard from './KanbanCard.vue';

const props = defineProps({
  column: {
    type: Object,
    required: true,
  },
  allColumns: {
    type: Array,
    default: () => []
  },
  pipelineId: {
    type: [String, Number],
    required: true
  }
});

const emit = defineEmits(['move-conversation', 'delete-conversation', 'update-conversation', 'drag-start', 'drag-end', 'load-more']);

const { t } = useI18n();

// Estado do menu de ordenação
const showSortMenu = ref(false);
const currentSort = ref('default'); // 'default', 'value_desc', 'value_asc', 'date_desc', 'date_asc', 'unread_first', 'read_first'

const columnStyle = computed(() => ({
  backgroundColor: props.column.color + '20', // 20% opacity
  borderColor: props.column.color,
}));

const handleDrop = (event) => {
  event.preventDefault();
  event.currentTarget.classList.remove('drag-over');
  
  const conversationId = event.dataTransfer.getData('conversation-id');
  const fromColumnId = event.dataTransfer.getData('from-column-id');
  
  if (conversationId && parseInt(fromColumnId) !== props.column.id) {
    emit('move-conversation', parseInt(conversationId), parseInt(fromColumnId), props.column.id);
  }
};

const handleDragOver = (event) => {
  event.preventDefault();
  event.currentTarget.classList.add('drag-over');
};

const handleDragLeave = (event) => {
  event.currentTarget.classList.remove('drag-over');
};


const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

// Computed para conversas ordenadas
const sortedConversations = computed(() => {
  const conversations = props.column.conversations || [];
  
  if (!conversations.length) return conversations;
  
  switch (currentSort.value) {
    case 'value_desc':
      return [...conversations].sort((a, b) => {
        const aValue = getConversationTotalValue(a);
        const bValue = getConversationTotalValue(b);
        return bValue - aValue;
      });
    
    case 'value_asc':
      return [...conversations].sort((a, b) => {
        const aValue = getConversationTotalValue(a);
        const bValue = getConversationTotalValue(b);
        return aValue - bValue;
      });
    
    case 'date_desc':
      return [...conversations].sort((a, b) => {
        const aDate = getKanbanCardCreatedAt(a);
        const bDate = getKanbanCardCreatedAt(b);
        return bDate - aDate;
      });
    
    case 'date_asc':
      return [...conversations].sort((a, b) => {
        const aDate = getKanbanCardCreatedAt(a);
        const bDate = getKanbanCardCreatedAt(b);
        return aDate - bDate;
      });
    
    case 'unread_first':
      return [...conversations].sort((a, b) => {
        const aUnread = a.unread_count > 0 ? 1 : 0;
        const bUnread = b.unread_count > 0 ? 1 : 0;
        if (aUnread !== bUnread) return bUnread - aUnread;
        // Se ambos têm mesmo status de leitura, ordenar por data de criação do card
        const aDate = getKanbanCardCreatedAt(a);
        const bDate = getKanbanCardCreatedAt(b);
        return bDate - aDate;
      });
    
    case 'read_first':
      return [...conversations].sort((a, b) => {
        const aRead = a.unread_count > 0 ? 0 : 1;
        const bRead = b.unread_count > 0 ? 0 : 1;
        if (aRead !== bRead) return bRead - aRead;
        // Se ambos têm mesmo status de leitura, ordenar por data de criação do card
        const aDate = getKanbanCardCreatedAt(a);
        const bDate = getKanbanCardCreatedAt(b);
        return bDate - aDate;
      });
    
    case 'name_asc':
      return [...conversations].sort((a, b) => {
        const aName = (a.contact?.name || a.contact?.email || '').toLowerCase();
        const bName = (b.contact?.name || b.contact?.email || '').toLowerCase();
        return aName.localeCompare(bName, 'pt-BR');
      });
    
    case 'name_desc':
      return [...conversations].sort((a, b) => {
        const aName = (a.contact?.name || a.contact?.email || '').toLowerCase();
        const bName = (b.contact?.name || b.contact?.email || '').toLowerCase();
        return bName.localeCompare(aName, 'pt-BR');
      });
    
    default:
      return conversations;
  }
});

// Função auxiliar para obter os valores totais por moeda da conversa (valores numéricos)
const getConversationValuesByCurrency = (conversation) => {
  // Buscar o card específico do pipeline atual
  const card = conversation?.kanban_cards?.find(card => 
    String(card.kanban_pipeline_id) === String(props.pipelineId)
  );
  
  if (!card?.kanban_card_products) {
    return {};
  }
  
  // Agrupar produtos por moeda e somar valores
  const totals = {};
  card.kanban_card_products.forEach(cardProduct => {
    // A moeda vem do produto dentro de kanban_card_product
    const currency = cardProduct.product?.currency || 'BRL';
    const totalPrice = parseFloat(cardProduct.total_price) || 0;
    
    if (!totals[currency]) {
      totals[currency] = 0;
    }
    totals[currency] += totalPrice;
  });
  
  return totals;
};

// Função auxiliar para obter o valor total numérico da conversa (para ordenação)
const getConversationTotalValue = (conversation) => {
  // Buscar o card específico do pipeline atual
  const card = conversation?.kanban_cards?.find(card => 
    String(card.kanban_pipeline_id) === String(props.pipelineId)
  );
  
  if (card?.total_products_value) {
    return card.total_products_value;
  }
  
  // Fallback para conversation_products se não houver kanban_card_products
  return conversation?.products_total_value || 0;
};

// Função auxiliar para obter a data de criação do card Kanban
const getKanbanCardCreatedAt = (conversation) => {
  // Buscar o card específico do pipeline atual
  const card = conversation?.kanban_cards?.find(card => 
    String(card.kanban_pipeline_id) === String(props.pipelineId)
  );
  
  if (card?.created_at) {
    return new Date(card.created_at).getTime();
  }
  
  // Fallback para a data da conversa se não houver card
  return new Date(conversation.created_at || conversation.updated_at || 0).getTime();
};

// Função auxiliar para formatar valores monetários
const formatCurrencyValue = (value, currency) => {
  const roundedValue = Math.round(value * 100) / 100;
  
  switch (currency) {
    case 'BRL':
      return `R$ ${roundedValue.toFixed(2).replace('.', ',')}`;
    case 'USD':
      return `$ ${roundedValue.toFixed(2)}`;
    case 'EUR':
      return `€ ${roundedValue.toFixed(2)}`;
    default:
      return `${currency} ${roundedValue.toFixed(2)}`;
  }
};

// Computed para calcular os valores totais da coluna por moeda
const columnTotalsByCurrency = computed(() => {
  const conversations = props.column.conversations || [];
  const totals = {};
  
  conversations.forEach(conversation => {
    const conversationTotals = getConversationValuesByCurrency(conversation);
    Object.entries(conversationTotals).forEach(([currency, numericValue]) => {
      if (!totals[currency]) {
        totals[currency] = 0;
      }
      totals[currency] += numericValue;
    });
  });
  
  // Formatar valores por moeda
  const result = {};
  Object.entries(totals).forEach(([currency, total]) => {
    result[currency] = formatCurrencyValue(total, currency);
  });
  
  return result;
});

// Computed para exibir o total formatado
const columnTotalValue = computed(() => {
  const totals = Object.values(columnTotalsByCurrency.value);
  return totals.length > 0 ? totals.join(', ') : '';
});

// Função para aplicar ordenação
const applySort = (sortType) => {
  currentSort.value = sortType;
  showSortMenu.value = false;
};

// Função para obter o label da ordenação atual
const getSortLabel = (sortType) => {
  const labels = {
    'default': 'Padrão',
    'value_desc': 'Maior Valor',
    'value_asc': 'Menor Valor',
    'date_desc': 'Mais Novo',
    'date_asc': 'Mais Antigo',
    'name_asc': 'A → Z',
    'name_desc': 'Z → A',
    'unread_first': 'Não Lido',
    'read_first': 'Lido'
  };
  return labels[sortType] || 'Padrão';
};

// Função para alternar o menu
const toggleSortMenu = (event) => {
  event.stopPropagation();
  showSortMenu.value = !showSortMenu.value;
};

// Função para fechar o menu ao clicar fora
const closeSortMenu = () => {
  showSortMenu.value = false;
};
</script>

<template>
  <div
    class="kanban-column w-96 bg-n-background rounded-lg border border-n-weak flex flex-col h-full flex-shrink-0"
    :style="columnStyle"
    @drop="handleDrop"
    @dragover="handleDragOver"
    @dragleave="handleDragLeave"
  >
    <!-- Column Header -->
    <div class="p-4 border-b border-n-weak flex-shrink-0">
      <div class="flex items-center justify-between">
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2">
            <h3 class="font-semibold text-n-slate-12">{{ column.name }}</h3>
            <span 
              v-if="currentSort !== 'default'"
              class="text-xs px-2 py-1 bg-n-blue-11/10 text-n-blue-11 rounded-full border border-n-blue-11/20"
              :title="`Ordenado por: ${getSortLabel(currentSort)}`"
            >
              {{ getSortLabel(currentSort) }}
            </span>
          </div>
          <p class="text-xs text-n-slate-11 mt-1">{{ column.description }}</p>
          <div v-if="columnTotalValue" class="mt-1">
            <span class="text-xs text-n-slate-11">
              Total: {{ columnTotalValue }}
            </span>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <span class="bg-n-solid-3 text-n-slate-11 text-xs px-2 py-1 rounded-full">
            {{ column.conversations?.length || 0 }}
          </span>
          
          <!-- Menu de Ordenação -->
          <div class="relative">
            <button 
              @click="toggleSortMenu"
              class="text-n-slate-10 hover:text-n-slate-12 transition-colors"
              :class="{ 'text-n-blue-11': showSortMenu }"
              title="Ordenar coluna"
            >
              <i class="i-lucide-more-horizontal" />
            </button>
            
            <!-- Dropdown Menu -->
            <div 
              v-if="showSortMenu"
              class="absolute right-0 top-8 z-50 w-48 bg-n-background border border-n-weak rounded-lg shadow-lg py-2"
              @click.stop
            >
              <div class="px-3 py-2 text-xs font-medium text-n-slate-11 border-b border-n-weak mb-2">
                Ordenar por:
              </div>
              
              <button
                @click="applySort('default')"
                class="w-full px-3 py-2 text-sm text-left hover:bg-n-slate-2 transition-colors flex items-center justify-between"
                :class="{ 'bg-n-blue-11/10 text-n-blue-11': currentSort === 'default' }"
              >
                <span>Padrão</span>
                <i v-if="currentSort === 'default'" class="i-lucide-check size-4" />
              </button>
              
              <div class="border-t border-n-weak my-1"></div>
              
              <button
                @click="applySort('value_desc')"
                class="w-full px-3 py-2 text-sm text-left hover:bg-n-slate-2 transition-colors flex items-center justify-between"
                :class="{ 'bg-n-blue-11/10 text-n-blue-11': currentSort === 'value_desc' }"
              >
                <span>Maior Valor</span>
                <i v-if="currentSort === 'value_desc'" class="i-lucide-check size-4" />
              </button>
              
              <button
                @click="applySort('value_asc')"
                class="w-full px-3 py-2 text-sm text-left hover:bg-n-slate-2 transition-colors flex items-center justify-between"
                :class="{ 'bg-n-blue-11/10 text-n-blue-11': currentSort === 'value_asc' }"
              >
                <span>Menor Valor</span>
                <i v-if="currentSort === 'value_asc'" class="i-lucide-check size-4" />
              </button>
              
              <div class="border-t border-n-weak my-1"></div>
              
              <button
                @click="applySort('date_desc')"
                class="w-full px-3 py-2 text-sm text-left hover:bg-n-slate-2 transition-colors flex items-center justify-between"
                :class="{ 'bg-n-blue-11/10 text-n-blue-11': currentSort === 'date_desc' }"
              >
                <span>Mais Novo</span>
                <i v-if="currentSort === 'date_desc'" class="i-lucide-check size-4" />
              </button>
              
              <button
                @click="applySort('date_asc')"
                class="w-full px-3 py-2 text-sm text-left hover:bg-n-slate-2 transition-colors flex items-center justify-between"
                :class="{ 'bg-n-blue-11/10 text-n-blue-11': currentSort === 'date_asc' }"
              >
                <span>Mais Antigo</span>
                <i v-if="currentSort === 'date_asc'" class="i-lucide-check size-4" />
              </button>
              
              <div class="border-t border-n-weak my-1"></div>
              
              <button
                @click="applySort('name_asc')"
                class="w-full px-3 py-2 text-sm text-left hover:bg-n-slate-2 transition-colors flex items-center justify-between"
                :class="{ 'bg-n-blue-11/10 text-n-blue-11': currentSort === 'name_asc' }"
              >
                <span>A → Z</span>
                <i v-if="currentSort === 'name_asc'" class="i-lucide-check size-4" />
              </button>
              
              <button
                @click="applySort('name_desc')"
                class="w-full px-3 py-2 text-sm text-left hover:bg-n-slate-2 transition-colors flex items-center justify-between"
                :class="{ 'bg-n-blue-11/10 text-n-blue-11': currentSort === 'name_desc' }"
              >
                <span>Z → A</span>
                <i v-if="currentSort === 'name_desc'" class="i-lucide-check size-4" />
              </button>
              
              <div class="border-t border-n-weak my-1"></div>
              
              <button
                @click="applySort('unread_first')"
                class="w-full px-3 py-2 text-sm text-left hover:bg-n-slate-2 transition-colors flex items-center justify-between"
                :class="{ 'bg-n-blue-11/10 text-n-blue-11': currentSort === 'unread_first' }"
              >
                <span>Não Lido</span>
                <i v-if="currentSort === 'unread_first'" class="i-lucide-check size-4" />
              </button>
              
              <button
                @click="applySort('read_first')"
                class="w-full px-3 py-2 text-sm text-left hover:bg-n-slate-2 transition-colors flex items-center justify-between"
                :class="{ 'bg-n-blue-11/10 text-n-blue-11': currentSort === 'read_first' }"
              >
                <span>Lido</span>
                <i v-if="currentSort === 'read_first'" class="i-lucide-check size-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Column Content -->
    <div class="flex-1 overflow-y-auto p-4 space-y-3">
      <div v-if="!column.conversations?.length" class="text-center py-8">
        <div class="text-n-slate-10 mb-2">
          <i class="i-lucide-message-circle text-2xl" />
        </div>
        <p class="text-sm text-n-slate-11">{{ t('KANBAN.NO_CONVERSATIONS') }}</p>
      </div>
      
      <KanbanCard
        v-for="conversation in sortedConversations"
        :key="conversation.id"
        :conversation="conversation"
        :column-id="column.id"
        :kanban-columns="allColumns"
        :pipeline-id="pipelineId"
        @delete-conversation="(id) => emit('delete-conversation', id)"
        @update-conversation="(conversation) => emit('update-conversation', conversation)"
        @drag-start="() => emit('drag-start')"
        @drag-end="() => emit('drag-end')"
      />
      
      <!-- Botão Ver Mais -->
      <div 
        v-if="column.pagination?.has_more && column.conversations?.length > 0"
        class="flex justify-center py-3"
      >
        <button
          @click="emit('load-more', column.id)"
          class="px-4 py-2 text-sm font-medium text-n-blue-11 hover:text-n-blue-12 bg-n-blue-11/10 hover:bg-n-blue-11/20 rounded-lg transition-colors flex items-center gap-2"
        >
          <i class="i-lucide-chevron-down" />
          <span>Ver mais ({{ column.pagination.total - column.conversations?.length || 0 }} restantes)</span>
        </button>
      </div>
    </div>
    
    <!-- Overlay para fechar menu ao clicar fora -->
    <div 
      v-if="showSortMenu" 
      class="fixed inset-0 z-40" 
      @click="closeSortMenu"
    ></div>
  </div>
</template>

<style scoped>
.kanban-column {
  transition: all 0.2s ease;
}

.kanban-column.drag-over {
  transform: scale(1.02);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* Scroll vertical customizado para as colunas */
.kanban-column .overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.kanban-column .overflow-y-auto::-webkit-scrollbar-track {
  background: rgba(156, 163, 175, 0.1);
  border-radius: 3px;
}

.kanban-column .overflow-y-auto::-webkit-scrollbar-thumb {
  background: rgba(156, 163, 175, 0.3);
  border-radius: 3px;
}

.kanban-column .overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: rgba(156, 163, 175, 0.5);
}
</style>